import { useEffect, useRef, useState } from "react";

import { api, ApiFailure } from "../api/client";
import type { MarkupValidation } from "../api/types";
import { Badge } from "./ui";

// Editor de IAMEDIA Markup con validación en vivo.
//
// La validación la hace el **backend** (`POST /api/v1/markup/validate`) y no
// este componente. Validar del lado del cliente exigiría mantener la gramática
// dos veces, y las dos acabarían divergiendo justo en el caso raro — que es
// cuando el tenant necesita que coincidan. Aquí la gramática es la misma que
// ejecuta las llamadas.
//
// Se valida con retraso (debounce) en lugar de a cada pulsación: sin eso,
// escribir un documento de treinta líneas serían trescientas peticiones.
const DEBOUNCE_MS = 400;

export function MarkupEditor({
  value,
  onChange,
  label = "IAMEDIA Markup",
  rows = 12,
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  rows?: number;
}) {
  const [validation, setValidation] = useState<MarkupValidation | null>(null);
  const [checking, setChecking] = useState(false);
  const [unreachable, setUnreachable] = useState<string | null>(null);

  // El timer va en una ref para que cada pulsación reemplace la validación
  // pendiente en lugar de encolar otra.
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    window.clearTimeout(timer.current);

    if (value.trim() === "") {
      setValidation(null);
      setUnreachable(null);
      return;
    }

    setChecking(true);
    timer.current = window.setTimeout(() => {
      void (async () => {
        try {
          const result = await api.post<MarkupValidation>(
            "/api/v1/markup/validate",
            { markup: value },
          );
          setValidation(result);
          setUnreachable(null);
        } catch (err) {
          // Un fallo de red no es un markup inválido: decirlo así mandaría al
          // tenant a buscar en el sitio equivocado.
          setValidation(null);
          setUnreachable(
            err instanceof ApiFailure ? err.error.message : "no se pudo validar",
          );
        } finally {
          setChecking(false);
        }
      })();
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(timer.current);
  }, [value]);

  const invalidLine = validation?.valid === false ? validation.error?.line : undefined;

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-medium text-slate-300">{label}</span>
        <Status checking={checking} validation={validation} unreachable={unreachable} />
      </div>

      <div className="mt-1 flex gap-2">
        {/* La columna de números de línea es lo que hace útil el error: el
            validador devuelve línea y columna, y sin verlas el tenant tiene
            que contarlas a mano. */}
        <LineNumbers text={value} rows={rows} highlight={invalidLine} />
        <textarea
          className={
            "min-h-0 w-full flex-1 rounded-md border px-3 py-2 font-mono text-xs " +
            "focus:outline-none focus:ring-1 " +
            (validation?.valid === false
              ? "border-rose-500/50 focus:border-rose-400 focus:ring-rose-400"
              : "border-white/15 focus:border-sky-400/60 focus:ring-sky-400/60")
          }
          rows={rows}
          spellCheck={false}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={'<Response>\n  <Pause length="2"/>\n  <Hangup/>\n</Response>'}
        />
      </div>

      {validation?.valid === false && validation.error && (
        <p className="mt-2 text-xs text-rose-300">
          <strong>Línea {validation.error.line}</strong>
          {validation.error.column > 0 && `, columna ${validation.error.column}`}:{" "}
          {validation.error.message}
        </p>
      )}

      {unreachable && (
        <p className="mt-2 text-xs text-amber-300">
          No se pudo validar: {unreachable}. El documento no se ha comprobado.
        </p>
      )}

      {validation?.valid && (
        <div className="mt-2 space-y-1 text-xs">
          {validation.verbs && validation.verbs.length > 0 && (
            <p className="text-slate-600">
              Verbos: <code>{validation.verbs.join(" → ")}</code>
            </p>
          )}

          {/* Un verbo que parsea y todavía no se ejecuta es información que el
              tenant necesita antes de desplegar: la llamada irá al fallback. */}
          {validation.not_yet_executable && validation.not_yet_executable.length > 0 && (
            <p className="text-amber-300">
              <strong>{validation.not_yet_executable.join(", ")}</strong> es válido pero
              todavía no se ejecuta: la llamada usará la URL de respaldo.
            </p>
          )}

          {validation.warnings?.map((warning, index) => (
            <p key={index} className="text-amber-300">
              Línea {warning.line}: {warning.verb}
              {warning.attribute && ` · ${warning.attribute}`} — {warning.message}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function Status({
  checking,
  validation,
  unreachable,
}: {
  checking: boolean;
  validation: MarkupValidation | null;
  unreachable: string | null;
}) {
  if (checking) return <span className="text-xs text-slate-500">Validando…</span>;
  if (unreachable) return <Badge tone="warn">Sin validar</Badge>;
  if (validation?.valid === true) return <Badge tone="good">Válido</Badge>;
  if (validation?.valid === false) return <Badge tone="bad">Inválido</Badge>;
  return null;
}

/**
 * LineNumbers pinta la columna de números alineada con el textarea.
 *
 * Se resalta la línea del error porque el validador la devuelve y es lo único
 * que convierte "XML inválido" en algo accionable sobre un documento de
 * cincuenta líneas.
 */
function LineNumbers({
  text,
  rows,
  highlight,
}: {
  text: string;
  rows: number;
  highlight?: number;
}) {
  const count = Math.max(text.split("\n").length, rows);

  return (
    <div
      aria-hidden
      className="select-none rounded-md bg-white/[0.04] px-2 py-2 text-right font-mono text-xs leading-5 text-slate-600"
    >
      {Array.from({ length: count }, (_, index) => index + 1).map((line) => (
        <div
          key={line}
          className={line === highlight ? "font-semibold text-rose-300" : undefined}
        >
          {line}
        </div>
      ))}
    </div>
  );
}

/** Ejemplos que el editor ofrece como punto de partida. */
export const MARKUP_EXAMPLES: { name: string; markup: string }[] = [
  {
    name: "Colgar",
    markup: "<Response>\n  <Hangup/>\n</Response>",
  },
  {
    name: "Esperar y colgar",
    markup: '<Response>\n  <Pause length="2"/>\n  <Hangup/>\n</Response>',
  },
  {
    name: "Redirigir",
    markup:
      "<Response>\n  <Redirect>https://cliente.example.com/siguiente</Redirect>\n</Response>",
  },
  {
    name: "Rechazar una entrante",
    markup: '<Response>\n  <Reject reason="busy"/>\n</Response>',
  },
];
